/**
 * Management demo — single fictional dataset.
 *
 * Everything here is SAMPLE data for a front-end demonstration. It is used only
 * to populate the management screens (editorial queue, analytics, activity feed,
 * charity concepts). Nothing is read from or written to the live Backend, and no
 * figure represents a real payment, visitor or submission.
 */

import type { StudioForm } from '@/pages/demo-admin/components/story-studio/studioData';
import { REGIONS } from '@/pages/demo/revenue/components/revenueData';
import type { Region } from '@/pages/demo/revenue/components/revenueData';

/* ------------------------------------------------------------------ */
/* Editorial workflow                                                  */
/* ------------------------------------------------------------------ */

export type EditorialStatus =
  | 'Incoming'
  | 'AI-prepared'
  | 'Needs review'
  | 'Revisions requested'
  | 'Approved'
  | 'Scheduled'
  | 'Published';

export const EDITORIAL_STATUSES: EditorialStatus[] = [
  'Incoming',
  'AI-prepared',
  'Needs review',
  'Revisions requested',
  'Approved',
  'Scheduled',
  'Published',
];

export type EditorialSource = 'Community submission' | 'AI discovery' | 'Course press release' | 'Reader tip' | 'Staff draft';

export type EditorialItem = {
  id: string;
  status: EditorialStatus;
  source: EditorialSource;
  sourceDetail: string;
  received: string;
  aiPrepared: boolean;
  aiConfidence: number;
  regionIds: string[];
  form: StudioForm;
};

const BODY_LONG_BEACH = `EL DORADO PARK — What began as a quiet Sunday-morning tradition has outgrown its bracket. With 214 junior golfers on the entry list, the Long Beach Junior Open will run two full days for the first time in its nineteen-year history.

Tournament director Ray Ortiz said the expansion was a response rather than a choice. "We had to turn families away last year for the first time," he said. "Adding the second round lets us keep everyone who qualified on the course instead of on a waitlist."

Proceeds again benefit the Long Beach Youth Sports Fund, which has covered entry fees for more than 400 local juniors over the past decade.`;

const BODY_OC = `NEWPORT BEACH — A youth golf program that started in a single Irvine park has expanded to six courses across Orange County this fall, organizers announced this week.

The program pairs experienced volunteers with first-time junior players, and runs on donated weekend tee times. Enrollment has tripled since it launched two years ago, prompting the addition of two dedicated coaching staff.

Registration for the fall session opens next month, with a portion of every entry supporting the scholarship fund.`;

const BODY_IE = `RIVERSIDE — The Valley Veterans Cup raised a record total for the local veterans' support network, organizers confirmed, drawing its largest field in eight years.

The four-person scramble filled in under a week, and a waiting list opened before the first tee time was drawn. Sponsors covered green fees so that every dollar of entry went directly to the beneficiary.`;

const BODY_SD = `CORONADO — Coastal courses around San Diego are reporting their strongest fall bookings in several seasons, a shift operators attribute to a mild stretch of weather and a wave of new league players.

Greens crews have kept surfaces firm through the shoulder season, and twilight availability has expanded at three public courses.`;

const BODY_PHX = `SCOTTSDALE — Desert golf season opened with record sign-ups across the Valley, with several courses reporting tee sheets filled through the first weekend of play.

Operators point to a longer booking window and renewed interest from winter visitors as the main drivers behind the early demand.`;

const BODY_TUC = `TUCSON — Sonoran-area courses are preparing for peak winter play, adding maintenance windows and expanding cart availability ahead of the busiest stretch of the calendar.

Two clubs have also introduced a new twilight rate aimed at newer players.`;

function item(partial: Omit<EditorialItem, 'form'> & { form: StudioForm }): EditorialItem {
  return partial;
}

export const EDITORIAL_QUEUE: EditorialItem[] = [
  item({
    id: 'ed-01',
    status: 'Needs review',
    source: 'Community submission',
    sourceDetail: 'Submitted via the public community form',
    received: '2026-10-08',
    aiPrepared: true,
    aiConfidence: 92,
    regionIds: ['los-angeles'],
    form: {
      headline: 'Long Beach Junior Open Expands to 36 Holes After Record Entries',
      author: 'Marisol Vega',
      category: 'Tournament Coverage',
      summary: 'A record 214 junior entries pushed the Long Beach Junior Open to a two-day, 36-hole format for 2026.',
      body: BODY_LONG_BEACH,
      image:
        'https://readdy.ai/api/search-image?query=Sunlit%20junior%20golf%20tournament%20on%20a%20well%20kept%20public%20course%20with%20players%20and%20flags%2C%20deep%20green%20editorial%20sports%20photography%2C%20soft%20morning%20light%2C%20clean%20composition%2C%20no%20text&width=1600&height=900&seq=gs-editorial-lbjr&orientation=landscape',
      seoTitle: 'Long Beach Junior Open Expands to 36 Holes | GreenSide News',
      metaDescription: 'A record 214 junior entries pushed the Long Beach Junior Open to a two-day, 36-hole format for 2026.',
      slug: 'long-beach-junior-open-expands-to-36-holes',
    },
  }),
  item({
    id: 'ed-02',
    status: 'AI-prepared',
    source: 'Course press release',
    sourceDetail: 'Received from El Dorado Park media desk',
    received: '2026-10-08',
    aiPrepared: true,
    aiConfidence: 88,
    regionIds: ['orange-county', 'los-angeles'],
    form: {
      headline: 'Youth Golf Program Expands to Six Orange County Courses',
      author: 'GreenSide Newsroom',
      category: 'Community',
      summary: 'A program that started in one Irvine park now runs on six courses across Orange County.',
      body: BODY_OC,
      image:
        'https://readdy.ai/api/search-image?query=Junior%20golfers%20practicing%20together%20on%20an%20Orange%20County%20course%20green%20with%20instructor%2C%20warm%20editorial%20lifestyle%20photography%2C%20soft%20natural%20light%2C%20clean%20background%2C%20no%20text&width=1600&height=900&seq=gs-editorial-oc-youth&orientation=landscape',
      seoTitle: 'Youth Golf Program Expands Across Orange County | GreenSide News',
      metaDescription: 'A youth golf program has expanded to six courses across Orange County, adding coaching staff and scholarships.',
      slug: 'youth-golf-program-expands-orange-county',
    },
  }),
  item({
    id: 'ed-03',
    status: 'Incoming',
    source: 'AI discovery',
    sourceDetail: 'Detected from a regional news feed — awaiting human review',
    received: '2026-10-09',
    aiPrepared: false,
    aiConfidence: 0,
    regionIds: ['inland-empire'],
    form: {
      headline: 'Valley Veterans Cup Raises Record Total in Riverside',
      author: '',
      category: 'Charity',
      summary: 'The Valley Veterans Cup drew its largest field in eight years.',
      body: BODY_IE,
      image:
        'https://readdy.ai/api/search-image?query=Charity%20golf%20scramble%20tournament%20scene%20on%20a%20Riverside%20course%20with%20players%20and%20a%20donation%20banner%2C%20warm%20documentary%20sports%20photography%2C%20natural%20afternoon%20light%2C%20no%20text&width=1600&height=900&seq=gs-editorial-ie-vets&orientation=landscape',
      seoTitle: '',
      metaDescription: '',
      slug: 'valley-veterans-cup-record-total',
    },
  }),
  item({
    id: 'ed-04',
    status: 'Revisions requested',
    source: 'Reader tip',
    sourceDetail: 'Editor asked for a verified booking figure',
    received: '2026-10-07',
    aiPrepared: true,
    aiConfidence: 74,
    regionIds: ['san-diego'],
    form: {
      headline: 'Coastal Courses Report Strongest Fall Bookings in Years',
      author: 'Devon Rios',
      category: 'Course News',
      summary: 'San Diego coastal courses are reporting their strongest fall bookings in several seasons.',
      body: BODY_SD,
      image:
        'https://readdy.ai/api/search-image?query=Coastal%20San%20Diego%20golf%20course%20fairway%20near%20the%20water%20at%20golden%20hour%2C%20editorial%20sports%20photography%2C%20calm%20ocean%20in%20the%20distance%2C%20warm%20tones%2C%20no%20text&width=1600&height=900&seq=gs-editorial-sd-coast&orientation=landscape',
      seoTitle: 'San Diego Courses Report Strong Fall Bookings | GreenSide News',
      metaDescription: 'Coastal San Diego courses are reporting their strongest fall bookings in several seasons.',
      slug: 'san-diego-strong-fall-bookings',
    },
  }),
  item({
    id: 'ed-05',
    status: 'Approved',
    source: 'Staff draft',
    sourceDetail: 'Approved by the editorial lead',
    received: '2026-10-06',
    aiPrepared: false,
    aiConfidence: 0,
    regionIds: ['phoenix'],
    form: {
      headline: 'Desert Golf Season Opens with Record Sign-ups',
      author: 'Priya Anand',
      category: 'Tournament Coverage',
      summary: 'Scottsdale-area courses opened the season with tee sheets filled through the first weekend.',
      body: BODY_PHX,
      image:
        'https://readdy.ai/api/search-image?query=Desert%20golf%20course%20in%20Scottsdale%20with%20saguaro%20cacti%20and%20manicured%20fairway%20at%20sunrise%2C%20warm%20editorial%20landscape%20photography%2C%20clean%20composition%2C%20no%20text&width=1600&height=900&seq=gs-editorial-phx-desert&orientation=landscape',
      seoTitle: 'Desert Golf Season Opens with Record Sign-ups | GreenSide News',
      metaDescription: 'Scottsdale-area courses opened the desert golf season with record sign-ups.',
      slug: 'desert-golf-season-record-signups',
    },
  }),
  item({
    id: 'ed-06',
    status: 'Scheduled',
    source: 'Course press release',
    sourceDetail: 'Scheduled for Thursday 6:00 AM',
    received: '2026-10-05',
    aiPrepared: true,
    aiConfidence: 90,
    regionIds: ['tucson'],
    form: {
      headline: 'Sonoran Courses Prepare for Peak Winter Play',
      author: 'GreenSide Newsroom',
      category: 'Course News',
      summary: 'Tucson-area courses are adding maintenance windows ahead of the busiest stretch of the calendar.',
      body: BODY_TUC,
      image:
        'https://readdy.ai/api/search-image?query=Sonoran%20desert%20golf%20course%20with%20rolling%20fairways%20and%20distant%20mountains%20in%20Tucson%20at%20soft%20morning%20light%2C%20editorial%20landscape%20photography%2C%20no%20text&width=1600&height=900&seq=gs-editorial-tuc-sonoran&orientation=landscape',
      seoTitle: 'Sonoran Courses Prepare for Peak Winter Play | GreenSide News',
      metaDescription: 'Tucson-area courses are preparing for peak winter play with new maintenance windows and twilight rates.',
      slug: 'sonoran-courses-peak-winter-play',
    },
  }),
  item({
    id: 'ed-07',
    status: 'Published',
    source: 'Community submission',
    sourceDetail: 'Published to the LA + OC editions',
    received: '2026-10-03',
    aiPrepared: true,
    aiConfidence: 95,
    regionIds: ['los-angeles', 'orange-county'],
    form: {
      headline: 'Metro Amateur Championship Returns to Rancho Park',
      author: 'Marisol Vega',
      category: 'Tournament Coverage',
      summary: 'The Metro Amateur Championship returns to Rancho Park with an expanded field.',
      body: BODY_LONG_BEACH,
      image:
        'https://readdy.ai/api/search-image?query=Amateur%20golf%20championship%20on%20a%20classic%20Los%20Angeles%20municipal%20course%20with%20gallery%20and%20flags%2C%20editorial%20sports%20photography%2C%20warm%20afternoon%20light%2C%20no%20text&width=1600&height=900&seq=gs-editorial-metro-am&orientation=landscape',
      seoTitle: 'Metro Amateur Championship Returns to Rancho Park | GreenSide News',
      metaDescription: 'The Metro Amateur Championship returns to Rancho Park with an expanded field.',
      slug: 'metro-amateur-returns-rancho-park',
    },
  }),
  item({
    id: 'ed-08',
    status: 'Incoming',
    source: 'AI discovery',
    sourceDetail: 'Detected from a charity event listing — awaiting human review',
    received: '2026-10-09',
    aiPrepared: false,
    aiConfidence: 0,
    regionIds: ['orange-county'],
    form: {
      headline: 'OC Business Cup Adds a Charity Division',
      author: '',
      category: 'Charity',
      summary: 'The OC Business Cup adds a dedicated charity division for this year.',
      body: BODY_OC,
      image:
        'https://readdy.ai/api/search-image?query=Corporate%20charity%20golf%20event%20on%20an%20Orange%20County%20course%20with%20teams%20and%20signage%2C%20clean%20editorial%20sports%20photography%2C%20soft%20natural%20light%2C%20no%20text&width=1600&height=900&seq=gs-editorial-oc-biz&orientation=landscape',
      seoTitle: '',
      metaDescription: '',
      slug: 'oc-business-cup-charity-division',
    },
  }),
];

/* ------------------------------------------------------------------ */
/* Central activity feed                                               */
/* ------------------------------------------------------------------ */

export type ActivityItem = {
  id: string;
  icon: string;
  title: string;
  detail: string;
  time: string;
  section: 'editorial' | 'tournaments' | 'bulletins' | 'revenue' | 'audience';
};

export const ACTIVITY: ActivityItem[] = [
  { id: 'ac-1', icon: 'ri-sparkling-line', title: 'AI prepared a draft', detail: '"Youth Golf Program Expands…" is ready for editorial review.', time: '9 min ago', section: 'editorial' },
  { id: 'ac-2', icon: 'ri-mail-unread-line', title: 'New sponsor inquiry', detail: 'Harbor Point Grill requested the Featured Sponsor package (LA).', time: '32 min ago', section: 'revenue' },
  { id: 'ac-3', icon: 'ri-pushpin-line', title: 'Bulletin awaiting moderation', detail: 'A community notice about a Saturday foursome needs review.', time: '1 hr ago', section: 'bulletins' },
  { id: 'ac-4', icon: 'ri-trophy-line', title: 'Tournament submitted', detail: 'Spring Charity Scramble was submitted for the tournament calendar.', time: '2 hrs ago', section: 'tournaments' },
  { id: 'ac-5', icon: 'ri-refresh-line', title: 'Renewal due soon', detail: 'OC Mortgage Group renews in 5 days.', time: '3 hrs ago', section: 'revenue' },
  { id: 'ac-6', icon: 'ri-user-add-line', title: 'Subscribers growing', detail: '+135 sample subscribers this month across all regions.', time: 'Yesterday', section: 'audience' },
];

/* ------------------------------------------------------------------ */
/* Regional analytics — one consistent dataset                         */
/* ------------------------------------------------------------------ */

export type RangeKey = '7d' | '30d' | '90d';

export const RANGES: { id: RangeKey; label: string; short: string }[] = [
  { id: '7d', label: 'Last 7 days', short: '7 days' },
  { id: '30d', label: 'Last 30 days', short: '30 days' },
  { id: '90d', label: 'Last 90 days', short: '90 days' },
];

export type RegionMetrics = {
  pageViews: number;
  visitors: number;
  sessions: number;
  newSubscribers: number;
  sponsorImpressions: number;
  sponsorClicks: number;
  tournamentViews: number;
  registrations: number;
};

/**
 * Base period = 30 days. Week and quarter figures are derived by a fixed ratio
 * so every range stays internally consistent and a region is only ever counted
 * once per metric (multi-region stories are attributed to their primary region).
 */
const BASE_30: Record<string, RegionMetrics> = {
  'los-angeles': { pageViews: 186000, visitors: 62000, sessions: 84000, newSubscribers: 312, sponsorImpressions: 118000, sponsorClicks: 3420, tournamentViews: 24000, registrations: 860 },
  'orange-county': { pageViews: 96000, visitors: 33000, sessions: 44000, newSubscribers: 176, sponsorImpressions: 61000, sponsorClicks: 1780, tournamentViews: 12500, registrations: 430 },
  'inland-empire': { pageViews: 64000, visitors: 22000, sessions: 29500, newSubscribers: 121, sponsorImpressions: 41000, sponsorClicks: 1120, tournamentViews: 8800, registrations: 300 },
  'san-diego': { pageViews: 104000, visitors: 35000, sessions: 47000, newSubscribers: 188, sponsorImpressions: 66000, sponsorClicks: 1930, tournamentViews: 14300, registrations: 500 },
  phoenix: { pageViews: 78000, visitors: 26000, sessions: 35000, newSubscribers: 142, sponsorImpressions: 49000, sponsorClicks: 1330, tournamentViews: 10400, registrations: 360 },
  tucson: { pageViews: 46000, visitors: 15500, sessions: 20800, newSubscribers: 84, sponsorImpressions: 28000, sponsorClicks: 760, tournamentViews: 6100, registrations: 210 },
};

const RANGE_FACTOR: Record<RangeKey, number> = { '7d': 7 / 30, '30d': 1, '90d': 3 };

function scaleMetrics(m: RegionMetrics, factor: number): RegionMetrics {
  const r = (n: number) => Math.round(n * factor);
  return {
    pageViews: r(m.pageViews),
    visitors: r(m.visitors),
    sessions: r(m.sessions),
    newSubscribers: r(m.newSubscribers),
    sponsorImpressions: r(m.sponsorImpressions),
    sponsorClicks: r(m.sponsorClicks),
    tournamentViews: r(m.tournamentViews),
    registrations: r(m.registrations),
  };
}

export function metricsFor(regionId: string, range: RangeKey): RegionMetrics {
  return scaleMetrics(BASE_30[regionId], RANGE_FACTOR[range]);
}

export function sumMetrics(list: RegionMetrics[]): RegionMetrics {
  return list.reduce(
    (acc, m) => ({
      pageViews: acc.pageViews + m.pageViews,
      visitors: acc.visitors + m.visitors,
      sessions: acc.sessions + m.sessions,
      newSubscribers: acc.newSubscribers + m.newSubscribers,
      sponsorImpressions: acc.sponsorImpressions + m.sponsorImpressions,
      sponsorClicks: acc.sponsorClicks + m.sponsorClicks,
      tournamentViews: acc.tournamentViews + m.tournamentViews,
      registrations: acc.registrations + m.registrations,
    }),
    { pageViews: 0, visitors: 0, sessions: 0, newSubscribers: 0, sponsorImpressions: 0, sponsorClicks: 0, tournamentViews: 0, registrations: 0 },
  );
}

function regionsByState(state: Region['state']): Region[] {
  return REGIONS.filter((r) => r.state === state);
}

/** Resolve a scope key to the list of regions it covers. */
export function regionsForScope(scope: string): Region[] {
  if (scope === 'all') return REGIONS;
  if (scope === 'california') return regionsByState('California');
  if (scope === 'arizona') return regionsByState('Arizona');
  const match = REGIONS.find((r) => r.id === scope);
  return match ? [match] : REGIONS;
}

export function metricsForScope(scope: string, range: RangeKey): RegionMetrics {
  return sumMetrics(regionsForScope(scope).map((r) => metricsFor(r.id, range)));
}

export function conversionRate(m: RegionMetrics): string {
  if (!m.visitors) return '0%';
  return `${((m.newSubscribers / m.visitors) * 100).toFixed(1)}%`;
}

export function sponsorCtr(m: RegionMetrics): string {
  if (!m.sponsorImpressions) return '—';
  return `${((m.sponsorClicks / m.sponsorImpressions) * 100).toFixed(1)}%`;
}

/* Aggregate demographic examples (sample only). */
export const DEMOGRAPHICS: { band: string; pct: number }[] = [
  { band: '25–34', pct: 21 },
  { band: '35–44', pct: 27 },
  { band: '45–54', pct: 24 },
  { band: '55–64', pct: 18 },
  { band: '65+', pct: 10 },
];

/* Top-performing stories (sample). Each is attributed to one primary region. */
export const TOP_STORIES: { title: string; regionId: string; category: string; views: number }[] = [
  { title: 'Long Beach Junior Open Expands to 36 Holes', regionId: 'los-angeles', category: 'Tournament Coverage', views: 18400 },
  { title: 'Coastal Courses Report Strongest Fall Bookings', regionId: 'san-diego', category: 'Course News', views: 12900 },
  { title: 'Youth Golf Program Expands Across Orange County', regionId: 'orange-county', category: 'Community', views: 11200 },
  { title: 'Desert Golf Season Opens with Record Sign-ups', regionId: 'phoenix', category: 'Tournament Coverage', views: 9800 },
  { title: 'Valley Veterans Cup Raises Record Total', regionId: 'inland-empire', category: 'Charity', views: 8700 },
  { title: 'Sonoran Courses Prepare for Peak Winter Play', regionId: 'tucson', category: 'Course News', views: 6400 },
];

/* ------------------------------------------------------------------ */
/* Charity fundraising concepts                                        */
/* ------------------------------------------------------------------ */

export const CHARITY_BY_REGION: Record<string, number> = {
  'los-angeles': 16400,
  'orange-county': 8200,
  'inland-empire': 5600,
  'san-diego': 9800,
  phoenix: 5400,
  tucson: 2800,
};

export const CHARITY_TOTAL_RAISED = Object.values(CHARITY_BY_REGION).reduce((a, b) => a + b, 0);

export type CharityProgram = {
  id: string;
  name: string;
  regionId: string;
  raised: number;
  goal: number;
  beneficiaries: string;
};

export const CHARITY_PROGRAMS: CharityProgram[] = [
  { id: 'ch-1', name: 'Long Beach Junior Open', regionId: 'los-angeles', raised: 9800, goal: 12000, beneficiaries: 'Youth sports fund' },
  { id: 'ch-2', name: 'Coastal Amateur Open', regionId: 'san-diego', raised: 7400, goal: 9000, beneficiaries: 'Junior clinic scholarships' },
  { id: 'ch-3', name: 'Youth Skills Classic', regionId: 'orange-county', raised: 5100, goal: 8000, beneficiaries: 'After-school golf program' },
  { id: 'ch-4', name: 'Valley Veterans Cup', regionId: 'inland-empire', raised: 5600, goal: 6000, beneficiaries: 'Veterans support network' },
  { id: 'ch-5', name: 'Desert Match Play', regionId: 'phoenix', raised: 3800, goal: 7000, beneficiaries: 'Youth caddie program' },
  { id: 'ch-6', name: 'Sonoran Summer Series', regionId: 'tucson', raised: 2800, goal: 5000, beneficiaries: 'Community course access' },
];

export const CHARITY_CONCEPTS: { icon: string; title: string; text: string }[] = [
  { icon: 'ri-hand-coin-line', title: 'Entry-fee donation split', text: 'An illustrative $10 of every entry can be routed straight to the tournament beneficiary.' },
  { icon: 'ri-shield-star-line', title: 'Sponsor matching', text: 'Sponsors can match a portion of the field — a package add-on shown as a concept.' },
  { icon: 'ri-line-chart-line', title: 'Live fundraising total', text: 'A public progress bar would show funds raised against each event goal.' },
  { icon: 'ri-receipt-line', title: 'Payout after the event', text: 'Funds are released to the beneficiary once the event closes and results are final.' },
];

export function regionName(id: string): string {
  return REGIONS.find((r) => r.id === id)?.city ?? id;
}